import { NgFor, NgIf } from '@angular/common';
import { Component, DoCheck, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ApiService } from '../../core/api.service';
import { dataLocalParaInput, LocalDatePipe } from '../../core/date-utils';
import { Contagem, ContagemItem, NotaTransferencia, NotaTransferenciaItem } from '../../core/models';
import { statusLabel } from '../../core/status-labels';

@Component({
  standalone: true,
  imports: [ReactiveFormsModule, NgFor, NgIf, RouterLink, LocalDatePipe],
  template: `
    <header class="page-header" *ngIf="nota">
      <div>
        <h2>Estoque do cliente</h2>
        <p>{{ nota.cliente }}</p>
        <span class="muted-line">Produtos: {{ produtosResumo }}</span>
      </div>
      <div class="header-actions">
        <a class="primary-link" [routerLink]="['/conferencias-entrada']" [queryParams]="{ ordem: nota.id_nota }" *ngIf="permiteMovimento()">Conferencia de entrada</a>
        <a class="primary-link" [routerLink]="['/contagens/nova']" [queryParams]="{ nota: nota.id_nota }" *ngIf="permiteMovimento()">Nova contagem</a>
      </div>
    </header>

    <div class="toast-stack" *ngIf="erro || mensagem">
      <div class="toast" [class.toast-error]="erro" [class.toast-success]="!erro && mensagem" role="alert">
        <span>{{ erro || mensagem }}</span>
        <button class="toast-close" type="button" (click)="fecharAviso()" aria-label="Fechar mensagem">X</button>
      </div>
    </div>

    <div class="modal-backdrop" *ngIf="mostrarModalDivergenciaFinalizacao">
      <section class="modal" role="dialog" aria-modal="true" aria-labelledby="divergencia-finalizacao-title">
        <header class="modal-header">
          <div>
            <h3 id="divergencia-finalizacao-title">Divergencias da finalizacao</h3>
            <p>Estes produtos serao enviados para Ocorrencias.</p>
          </div>
          <button class="ghost modal-close" type="button" (click)="fecharModalDivergenciaFinalizacao()" aria-label="Fechar">X</button>
        </header>

        <table class="size-table">
          <thead><tr><th>Produto</th><th>Tamanho</th><th>Conferido</th><th>Contado</th><th>Divergencia</th></tr></thead>
          <tbody>
            <tr *ngFor="let item of divergenciasFinalizacao">
              <td data-label="Produto">{{ item.codigo_produto }} - {{ item.produto }}</td>
              <td data-label="Tamanho">{{ item.tamanho }}</td>
              <td data-label="Conferido">{{ item.total_conferido }}</td>
              <td data-label="Contado">{{ item.total_contado }}</td>
              <td data-label="Divergencia">{{ diferencaConferenciaContagem(item) }}</td>
            </tr>
          </tbody>
        </table>

        <div class="form-actions">
          <button type="button" (click)="confirmarFinalizacaoComDivergencia()">Finalizar e criar ocorrencias</button>
          <button class="ghost" type="button" (click)="fecharModalDivergenciaFinalizacao()">Cancelar</button>
        </div>
      </section>
    </div>

    <section class="metrics" *ngIf="nota">
      <article><span>Total conferido</span><strong>{{ nota.total_conferido }}</strong></article>
      <article><span>Total contado</span><strong>{{ nota.total_contado }}</strong></article>
      <article><span>Falta contar</span><strong>{{ totalFaltaContar }}</strong></article>
      <article><span>Total retirado</span><strong>{{ nota.total_retirado }}</strong></article>
      <article><span>Saldo no estoque</span><strong>{{ nota.saldo_disponivel }}</strong></article>
    </section>

    <section class="panel view" *ngIf="nota">
      <dl>
        <div><dt>Status</dt><dd>{{ statusLabel(nota.status) }}</dd></div>
        <div><dt>Data recebimento</dt><dd>{{ nota.data_recebimento | localDate }}</dd></div>
        <div><dt>Cor</dt><dd>{{ nota.cor }}</dd></div>
        <div><dt>Observacao</dt><dd>{{ nota.observacao }}</dd></div>
        <div *ngIf="nota.observacao_divergencia"><dt>Observacao divergencia</dt><dd>{{ nota.observacao_divergencia }}</dd></div>
      </dl>
    </section>

    <section class="panel" *ngIf="nota && mostrarJustificativaDivergencia">
      <h3>Divergencias da finalizacao</h3>
      <table class="size-table">
        <thead><tr><th>Produto</th><th>Tamanho</th><th>Conferido</th><th>Contado</th><th>Diferenca</th></tr></thead>
        <tbody>
          <tr *ngFor="let item of divergenciasFinalizacao">
            <td data-label="Produto">{{ item.codigo_produto }} - {{ item.produto }}</td>
            <td data-label="Tamanho">{{ item.tamanho }}</td>
            <td data-label="Conferido">{{ item.total_conferido }}</td>
            <td data-label="Contado">{{ item.total_contado }}</td>
            <td data-label="Diferença">{{ diferencaConferenciaContagem(item) }}</td>
          </tr>
        </tbody>
      </table>
      <form [formGroup]="divergenciaForm" (ngSubmit)="finalizarComDivergencia()" novalidate>
        <div class="grid-form">
          <label class="full">Observacao da divergencia<textarea rows="3" formControlName="observacao_divergencia"></textarea></label>
        </div>
        <div class="form-actions">
          <button type="submit">Finalizar com divergencia</button>
          <button class="ghost" type="button" (click)="mostrarJustificativaDivergencia = false">Cancelar</button>
        </div>
      </form>
    </section>

    <nav class="tabs" *ngIf="nota">
      <button type="button" class="tab-button" [class.active]="abaAtiva === 'saldos'" (click)="abaAtiva = 'saldos'">Saldos</button>
      <button type="button" class="tab-button" [class.active]="abaAtiva === 'conferencias'" (click)="abaAtiva = 'conferencias'">Conferencias</button>
      <button type="button" class="tab-button" [class.active]="abaAtiva === 'contagens'" (click)="abaAtiva = 'contagens'">Contagens</button>
    </nav>

    <section class="panel" *ngIf="nota && abaAtiva === 'saldos'">
      <h3>Saldos por tamanho</h3>
      <table class="size-table">
        <thead><tr><th>Produto</th><th>Tamanho</th><th>Previsto</th><th>Valor R$</th><th>Conferido</th><th>Contado</th><th>Retirado</th><th>Saldo</th></tr></thead>
        <tbody>
          <tr *ngFor="let item of nota.saldos">
            <td data-label="Produto">{{ item.codigo_produto }} - {{ item.produto }}</td>
            <td data-label="Tamanho">{{ item.tamanho }}</td>
            <td data-label="Previsto">{{ item.quantidade_nota }}</td>
            <td data-label="Valor R$">{{ formatValor(item.valor_unitario) }}</td>
            <td data-label="Conferido">{{ item.total_conferido }}</td>
            <td data-label="Contado">{{ item.total_contado }}</td>
            <td data-label="Retirado">{{ item.total_retirado }}</td>
            <td data-label="Saldo">{{ item.saldo_disponivel }}</td>
          </tr>
        </tbody>
      </table>
    </section>

    <section class="panel" *ngIf="false">
      <h3>Registrar retirada por contagens</h3>
      <form [formGroup]="retiradaForm" (ngSubmit)="registrarRetirada()" novalidate>
        <div class="grid-form">
          <label>Data retirada<input type="date" formControlName="data_retirada"></label>
          <label>Responsavel entrega<input formControlName="responsavel"></label>
          <label class="full">Observacao<textarea rows="2" formControlName="observacao"></textarea></label>
        </div>

        <table class="data-table">
          <thead><tr><th></th><th>Contagem</th><th>Data</th><th>Responsaveis</th><th>Total</th><th>Status</th></tr></thead>
          <tbody>
            <tr *ngFor="let contagem of contagensDisponiveis" [class.selected-row]="contagemSelecionada(contagem.id_contagem)">
              <td data-label="Selecionar"><input type="checkbox" [checked]="contagemSelecionada(contagem.id_contagem)" (change)="alternarContagem(contagem, $any($event.target).checked)"></td>
              <td data-label="Contagem">{{ contagem.id_contagem }}</td>
              <td data-label="Data">{{ contagem.data_contagem | localDate }}</td>
              <td data-label="Responsáveis">{{ contagem.responsaveis }}</td>
              <td data-label="Total">{{ contagem.total_pecas }}</td>
              <td data-label="Status">{{ statusLabel(contagem.status) }}</td>
            </tr>
          </tbody>
        </table>
        <p class="field-hint" *ngIf="!contagensDisponiveis.length">Nenhuma contagem finalizada disponivel</p>

        <table class="size-table" *ngIf="resumoRetirada.length">
          <thead><tr><th>Produto</th><th>Tamanho</th><th>Contado</th><th>Saldo conferido</th></tr></thead>
          <tbody>
            <tr *ngFor="let item of resumoRetirada">
              <td data-label="Produto">{{ item.codigo_produto }} - {{ item.produto }}</td>
              <td data-label="Tamanho">{{ item.tamanho }}</td>
              <td data-label="Contado">{{ item.quantidade }}</td>
              <td data-label="Saldo conferido">{{ saldoTamanho(item.id_produto || 0, item.tamanho || '') }}</td>
            </tr>
          </tbody>
        </table>

        <div class="form-actions">
          <button type="submit" [disabled]="!contagensRetirada.length">Registrar retirada</button>
        </div>
      </form>
    </section>

    <section class="panel" *ngIf="nota && abaAtiva === 'conferencias'">
      <h3>Conferencias de entrada</h3>
      <table class="data-table">
        <thead><tr><th>Codigo</th><th>Faccao</th><th>Data</th><th>Responsavel</th><th>Total</th><th>Status</th><th></th></tr></thead>
        <tbody>
          <tr *ngFor="let conferencia of nota.conferencias">
            <td data-label="Código">{{ conferencia.id_conferencia }}</td>
            <td data-label="Facção">{{ conferencia.faccao }}</td>
            <td data-label="Data">{{ conferencia.data_conferencia | localDate }}</td>
            <td data-label="Responsável">{{ conferencia.responsavel }}</td>
            <td data-label="Total">{{ conferencia.total_pecas }}</td>
            <td data-label="Status">{{ statusLabel(conferencia.status) }}</td>
            <td class="actions" data-label="Ações">
              <a [routerLink]="['/conferencias-entrada']" [queryParams]="{ ordem: nota.id_nota }">Abrir</a>
              <button type="button" (click)="imprimirConferencia(conferencia.id_conferencia)">PDF</button>
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <section class="panel" *ngIf="nota && abaAtiva === 'contagens'">
      <h3>Contagens do estoque</h3>
      <table class="data-table">
        <thead><tr><th>Codigo</th><th>Data</th><th>Responsaveis</th><th>Total</th><th>Status</th><th></th></tr></thead>
        <tbody>
          <tr *ngFor="let contagem of nota.contagens">
            <td data-label="Código">{{ contagem.id_contagem }}</td>
            <td data-label="Data">{{ contagem.data_contagem | localDate }}</td>
            <td data-label="Responsáveis">{{ contagem.responsaveis }}</td>
            <td data-label="Total">{{ contagem.total_pecas }}</td>
            <td data-label="Status">{{ statusLabel(contagem.status) }}</td>
            <td class="actions" data-label="Ações">
              <a [routerLink]="['/contagens', contagem.id_contagem]">Visualizar</a>
              <button type="button" (click)="imprimirContagem(contagem.id_contagem)">PDF</button>
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <section class="panel" *ngIf="false">
      <h3>Retiradas</h3>
      <table class="data-table">
        <thead><tr><th>Data</th><th>Contagem</th><th>Responsavel</th><th>Total</th><th>Observacao</th><th></th></tr></thead>
        <tbody>
          <tr *ngFor="let retirada of $any(nota).retiradas || []">
            <td data-label="Data">{{ retirada.data_retirada | localDate }}</td>
            <td data-label="Contagem">{{ contagensRetiradaLabel(retirada) }}</td>
            <td data-label="Responsável">{{ retirada.responsavel }}</td>
            <td data-label="Total">{{ totalRetirada(retirada.itens) }}</td>
            <td data-label="Observação">{{ retirada.observacao }}</td>
            <td class="actions" data-label="Ações"><button class="ghost" type="button" (click)="imprimirRetirada(retirada.id_retirada)">Imprimir</button></td>
          </tr>
        </tbody>
      </table>
    </section>
  `
})
export class NotaDetalheComponent implements OnInit, DoCheck, OnDestroy {
  private api = inject(ApiService);
  private route = inject(ActivatedRoute);
  private fb = inject(FormBuilder);
  nota?: NotaTransferencia;
  contagensRetirada: Contagem[] = [];
  erro = '';
  mensagem = '';
  mostrarJustificativaDivergencia = false;
  mostrarModalDivergenciaRetirada = false;
  mostrarModalDivergenciaFinalizacao = false;
  abaAtiva: 'saldos' | 'retirada' | 'conferencias' | 'contagens' | 'retiradas' = 'saldos';
  statusLabel = statusLabel;
  private avisoAtual = '';
  private avisoTimer?: ReturnType<typeof setTimeout>;

  retiradaForm = this.fb.nonNullable.group({
    data_retirada: [dataLocalParaInput(), Validators.required],
    responsavel: ['', Validators.required],
    observacao: ['']
  });

  divergenciaForm = this.fb.nonNullable.group({
    observacao_divergencia: ['', Validators.required]
  });

  ngOnInit() {
    this.carregar();
  }

  ngDoCheck() {
    const aviso = this.erro || this.mensagem;
    if (aviso === this.avisoAtual) return;

    this.avisoAtual = aviso;
    this.agendarFechamentoAviso(aviso);
  }

  ngOnDestroy() {
    if (this.avisoTimer) clearTimeout(this.avisoTimer);
  }

  fecharAviso() {
    this.erro = '';
    this.mensagem = '';
    this.avisoAtual = '';
    if (this.avisoTimer) clearTimeout(this.avisoTimer);
  }

  carregar() {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    this.api.nota(id).subscribe((nota) => {
      this.nota = nota;
      if (!this.permiteMovimento()) this.mostrarJustificativaDivergencia = false;
      if (this.abaAtiva === 'retirada' && !this.permiteMovimento()) this.abaAtiva = 'saldos';
      this.resetRetirada();
    });
  }

  registrarRetirada(confirmarDivergencia = false) {
    if (!this.nota || !this.contagensRetirada.length) return;
    this.erro = '';
    this.mensagem = '';
    this.retiradaForm.markAllAsTouched();
    const raw = this.retiradaForm.getRawValue();
    if (!String(raw.responsavel || '').trim()) {
      this.erro = 'Informe o responsavel pela entrega';
      return;
    }

    const total = this.resumoRetirada.reduce((sum, item) => sum + Number(item.quantidade || 0), 0);
    const contagens = this.contagensRetirada.map((contagem) => contagem.id_contagem).join(', ');
    if (this.divergenciasRetirada.length && !confirmarDivergencia) {
      this.mostrarModalDivergenciaRetirada = true;
      return;
    }

    if (!this.divergenciasRetirada.length) {
      const confirmar = window.confirm(`Confirmar retirada das contagens ${contagens}, totalizando ${total} pecas?`);
      if (!confirmar) return;
    }

    const payload = {
      ...raw,
      id_contagens: this.contagensRetirada.map((contagem) => contagem.id_contagem)
    };

    this.api.registrarRetirada(this.nota.id_nota, payload).subscribe({
      next: (retirada) => {
        this.mensagem = 'Retirada registrada com sucesso';
        this.mostrarModalDivergenciaRetirada = false;
        this.imprimirRetirada(retirada.id_retirada);
        this.carregar();
      },
      error: (error) => (this.erro = error.error?.message || 'Erro ao registrar retirada')
    });
  }

  finalizar() {
    if (!this.nota) return;
    this.erro = '';
    this.mensagem = '';

    if (this.totalEstoqueARetirarFinalizacao > 0) {
      this.mostrarJustificativaDivergencia = false;
      this.mostrarModalDivergenciaFinalizacao = false;
      this.abaAtiva = 'saldos';
      this.erro = `Ainda ha estoque a retirar nesta ordem (${this.totalEstoqueARetirarFinalizacao} pecas). Use a aba Retiradas em Ordens de entrada antes de finalizar.`;
      return;
    }

    if (this.divergenciasFinalizacao.length) {
      this.mostrarJustificativaDivergencia = false;
      this.mostrarModalDivergenciaFinalizacao = true;
      return;
    }

    this.enviarFinalizacao();
  }

  finalizarComDivergencia() {
    if (!this.nota) return;
    this.erro = '';
    this.mensagem = '';
    this.divergenciaForm.markAllAsTouched();

    if (this.totalEstoqueARetirarFinalizacao > 0) {
      this.abaAtiva = 'saldos';
      this.erro = `Ainda ha estoque a retirar nesta ordem (${this.totalEstoqueARetirarFinalizacao} pecas). Use a aba Retiradas em Ordens de entrada antes de finalizar.`;
      return;
    }

    const observacao = String(this.divergenciaForm.getRawValue().observacao_divergencia || '').trim();
    if (!observacao) {
      this.erro = 'Informe a observacao da divergencia';
      return;
    }

    this.enviarFinalizacao({ observacao_divergencia: observacao }, 'Ordem finalizada com divergencia');
  }

  confirmarFinalizacaoComDivergencia() {
    this.enviarFinalizacao(
      { observacao_divergencia: 'Ocorrencias geradas automaticamente na finalizacao da ordem' },
      'Ordem finalizada com divergencia e ocorrencias geradas'
    );
  }

  fecharModalDivergenciaFinalizacao() {
    this.mostrarModalDivergenciaFinalizacao = false;
  }

  private enviarFinalizacao(payload: { observacao_divergencia?: string } = {}, mensagem = 'Ordem finalizada com sucesso') {
    if (!this.nota) return;

    this.api.finalizarNota(this.nota.id_nota, payload).subscribe({
      next: () => {
        this.mostrarJustificativaDivergencia = false;
        this.mostrarModalDivergenciaFinalizacao = false;
        this.divergenciaForm.reset({ observacao_divergencia: '' });
        this.mensagem = mensagem;
        this.carregar();
      },
      error: (error) => (this.erro = error.error?.message || 'Erro ao finalizar ordem')
    });
  }

  reabrir() {
    if (!this.nota) return;
    const confirmar = window.confirm(`Reabrir a ordem ${this.nota.numero_nota}?`);
    if (!confirmar) return;

    this.erro = '';
    this.mensagem = '';
    this.api.reabrirNota(this.nota.id_nota).subscribe({
      next: () => {
        this.mensagem = 'Ordem reaberta com sucesso';
        this.carregar();
      },
      error: (error) => (this.erro = error.error?.message || 'Erro ao reabrir ordem')
    });
  }

  cancelar() {
    if (!this.nota) return;
    const confirmar = window.confirm(`Cancelar a ordem ${this.nota.numero_nota}? Esta acao nao podera ser desfeita.`);
    if (!confirmar) return;

    this.api.cancelarNota(this.nota.id_nota).subscribe(() => this.carregar());
  }

  permiteMovimento() {
    return !!this.nota && !['CONFERIDA', 'DIVERGENTE', 'FINALIZADA', 'CANCELADA'].includes(this.nota.status);
  }

  permiteReabrir() {
    return !!this.nota && ['CONFERIDA', 'DIVERGENTE', 'FINALIZADA'].includes(this.nota.status);
  }

  saldoTamanho(idProduto: number, tamanho: string) {
    return this.nota?.saldos?.find((item) => Number(item.id_produto || 0) === Number(idProduto || 0) && item.tamanho === tamanho)?.saldo_disponivel || 0;
  }

  alternarContagem(contagem: Contagem, selecionada: boolean) {
    this.erro = '';
    this.mensagem = '';
    this.mostrarModalDivergenciaRetirada = false;
    if (!selecionada) {
      this.contagensRetirada = this.contagensRetirada.filter((item) => item.id_contagem !== contagem.id_contagem);
      return;
    }
    if (this.contagemSelecionada(contagem.id_contagem)) return;

    this.api.contagem(contagem.id_contagem).subscribe({
      next: (detalhe) => {
        this.contagensRetirada = [...this.contagensRetirada, detalhe].sort((a, b) => b.id_contagem - a.id_contagem);
      },
      error: (error) => (this.erro = error.error?.message || 'Erro ao carregar contagem')
    });
  }

  contagemSelecionada(idContagem: number) {
    return this.contagensRetirada.some((contagem) => contagem.id_contagem === idContagem);
  }

  totalRetirada(itens: NotaTransferenciaItem[] = []) {
    return itens.reduce((sum, item) => sum + Number(item.quantidade || 0), 0);
  }

  totalItemContagem(item: ContagemItem) {
    return Number(item.total ?? Number(item.quantidade || 0) + Number(item.defeitos || 0));
  }

  diferencaConferenciaContagem(item: NotaTransferenciaItem) {
    return Number(item.total_conferido || 0) - Number(item.total_contado || 0);
  }

  quantidadeDivergenteRetirada(item: NotaTransferenciaItem) {
    return Math.max(0, Number(item.quantidade || 0) - Number(item.saldo_disponivel || 0));
  }

  confirmarRetiradaComDivergencia() {
    this.registrarRetirada(true);
  }

  fecharModalDivergenciaRetirada() {
    this.mostrarModalDivergenciaRetirada = false;
  }

  imprimirRetirada(idRetirada: number) {
    if (!this.nota) return;
    this.api.abrirRetirada(this.nota.id_nota, idRetirada);
  }

  imprimirConferencia(idConferencia: number) {
    this.api.abrirConferenciaEntrada(idConferencia);
  }

  imprimirContagem(idContagem: number) {
    this.api.abrirRomaneio(idContagem);
  }

  contagensRetiradaLabel(retirada: { id_contagem?: number; id_contagens?: number[] }) {
    return (retirada.id_contagens?.length ? retirada.id_contagens : [retirada.id_contagem].filter(Boolean)).join(', ');
  }

  formatValor(value: string | number | null | undefined) {
    const text = String(value || '0').replace(/\s/g, '').replace(/^R\$/i, '');
    const normalized = text.includes(',') ? text.replace(/\./g, '').replace(',', '.') : text;
    const parsed = Number(normalized || 0);
    return `R$${(Number.isFinite(parsed) ? parsed : 0).toFixed(2).replace('.', ',')}`;
  }

  get produtosResumo() {
    if (!this.nota) return '';
    const produtos = new Map<number, string>();
    (this.nota.itens || this.nota.saldos || []).forEach((item) => {
      const idProduto = Number(item.id_produto || 0);
      if (!idProduto || produtos.has(idProduto)) return;
      produtos.set(idProduto, [item.codigo_produto, item.produto].filter(Boolean).join(' - '));
    });

    return [...produtos.values()].filter(Boolean).join(', ') || this.nota.produtos_resumo || [this.nota.codigo_produto, this.nota.produto].filter(Boolean).join(' - ');
  }

  get contagensDisponiveis() {
    const retiradas = new Set(
      (this.nota?.retiradas || [])
        .flatMap((retirada) => retirada.id_contagens?.length ? retirada.id_contagens : [retirada.id_contagem])
        .map((id) => Number(id || 0))
        .filter(Boolean)
    );
    return (this.nota?.contagens || []).filter((contagem) => contagem.status === 'FINALIZADA' && !retiradas.has(contagem.id_contagem));
  }

  get resumoRetirada() {
    const resumo = new Map<string, NotaTransferenciaItem>();
    this.contagensRetirada.flatMap((contagem) => contagem.itens || []).forEach((item) => {
      const key = `${Number(item.id_produto || 0)}|${item.tamanho || ''}`;
      const atual = resumo.get(key) || {
        id_produto: item.id_produto,
        codigo_produto: item.codigo_produto,
        produto: item.produto,
        tamanho: item.tamanho,
        quantidade: 0
      };
      atual.quantidade += this.totalItemContagem(item);
      resumo.set(key, atual);
    });

    return [...resumo.values()].sort((a, b) => {
      const produto = String(a.codigo_produto || '').localeCompare(String(b.codigo_produto || ''));
      return produto || String(a.tamanho || '').localeCompare(String(b.tamanho || ''));
    });
  }

  get divergenciasRetirada() {
    return this.resumoRetirada
      .map((item) => ({
        ...item,
        saldo_disponivel: this.saldoTamanho(item.id_produto || 0, item.tamanho || '')
      }))
      .filter((item) => this.quantidadeDivergenteRetirada(item) > 0);
  }

  get divergenciasFinalizacao() {
    return (this.nota?.saldos || []).filter((item) => this.diferencaConferenciaContagem(item) !== 0);
  }

  get totalFaltaContar() {
    if (!this.nota) return 0;
    if (this.nota.saldos?.length) {
      return this.nota.saldos.reduce((sum, item) => {
        return sum + Math.max(0, Number(item.total_conferido || 0) - Number(item.total_contado || 0));
      }, 0);
    }

    return Math.max(0, Number(this.nota.total_conferido || 0) - Number(this.nota.total_contado || 0));
  }

  get totalEstoqueARetirarFinalizacao() {
    return (this.nota?.saldos || []).reduce((sum, item) => {
      const conferido = Number(item.total_conferido || 0);
      const contado = Number(item.total_contado || 0);
      const retirado = Number(item.total_retirado || 0);
      return sum + Math.max(0, Math.min(conferido, contado) - retirado);
    }, 0);
  }

  private resetRetirada() {
    this.contagensRetirada = [];
    this.retiradaForm.reset({
      data_retirada: dataLocalParaInput(),
      responsavel: '',
      observacao: ''
    });
  }

  private agendarFechamentoAviso(aviso: string) {
    if (this.avisoTimer) clearTimeout(this.avisoTimer);
    if (!aviso) return;

    this.avisoTimer = setTimeout(() => this.fecharAviso(), 20000);
  }
}
